import { Link, usePage } from "@inertiajs/react";
import { Check, ChevronsUpDown, Plus, Settings } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { cn } from "@/lib/utils";
import { newOrganizationPath, organizationDashboardPath } from "@/routes";
import type { SharedData } from "@/types";

export function OrgSwitcher() {
  const { auth } = usePage<SharedData>().props;
  const { current_organization, organizations } = auth;

  if (!current_organization) {
    return null;
  }

  const routes = useOrganizationRoutes();

  const hasMultipleOrgs = organizations.length > 1;

  const OrgIcon = ({
    logoUrl,
    name,
  }: {
    logoUrl?: string | null;
    name: string;
  }) =>
    logoUrl ? (
      <img
        src={logoUrl}
        alt=""
        className="h-6 w-6 shrink-0 rounded object-cover"
      />
    ) : (
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary text-xs font-semibold text-primary-foreground">
        {name.charAt(0).toUpperCase()}
      </div>
    );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:!p-0"
            >
              <OrgIcon
                logoUrl={current_organization.logo_url}
                name={current_organization.name}
              />
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">
                  {current_organization.name}
                </span>
              </div>
              {hasMultipleOrgs && (
                <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 group-data-[collapsible=icon]:hidden" />
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Organizaciones
            </DropdownMenuLabel>

            {organizations.map((org) => (
              <DropdownMenuItem
                key={org.id}
                className={cn(
                  "gap-2 p-2 cursor-pointer",
                  org.id === current_organization.id && "bg-accent",
                )}
                asChild
              >
                <Link href={organizationDashboardPath(org.slug)} preserveScroll>
                  <OrgIcon logoUrl={org.logo_url} name={org.name} />
                  <span className="flex-1 truncate">{org.name}</span>
                  {org.id === current_organization.id && (
                    <Check className="h-4 w-4 shrink-0" />
                  )}
                </Link>
              </DropdownMenuItem>
            ))}

            <DropdownMenuSeparator />

            <DropdownMenuItem className="gap-2 p-2 cursor-pointer" asChild>
              <Link href={routes.settings()}>
                <Settings className="h-4 w-4" />
                Configuración
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem className="gap-2 p-2 cursor-pointer" asChild>
              <Link href={newOrganizationPath()}>
                <Plus className="h-4 w-4" />
                Crear organización
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
